import React, { useCallback, useEffect, useMemo, useRef, type ReactElement } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import type { ReferenceLoader } from '@atlassian/jira-bulk-operations-common/src/utils/reference-loader/use-reference-loader/index.tsx';
import { useSidebar } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/index.tsx';
import { useBulkSelectionStore } from '@atlassian/jira-business-list/src/controllers/bulk-selection/bulk-selection-store/index.tsx';

const useOnUnmount = (fn: () => void) => {
	const currentFn = useRef(fn);

	currentFn.current = fn;

	useEffect(() => () => currentFn.current(), []);
};

type Props = {
	bulkOperationsForm: ReferenceLoader;
};

export const BulkEditSidebar = ({ bulkOperationsForm }: Props) => {
	const [{ content }, { resetSidebarQueryParams, setSidebarContent: _setSidebarContent }] =
		useSidebar();
	const [{ showBulkEditSidebar }] = useBulkSelectionStore();
	const lastSetSidebarContent = useRef<ReactElement | null>(null);

	const setSidebarContent = useCallback<typeof _setSidebarContent>(
		(newContent) => {
			_setSidebarContent(newContent);
			lastSetSidebarContent.current = newContent;
		},
		[_setSidebarContent],
	);

	const resetSidebarContent = useCallback(() => {
		if (lastSetSidebarContent.current) {
			setSidebarContent(null);
		}
	}, [setSidebarContent]);

	useOnUnmount(resetSidebarContent);

	const sidebarContent = useMemo(
		() =>
			showBulkEditSidebar ? (
				<Box
					data-testId="business-list.ui.list-view.bulk-edit-sidebar.wrapper"
					xcss={wrapperStyles}
				>
					<Box
						data-testId="business-list.ui.list-view.bulk-edit-sidebar.bulkops-sidebar"
						ref={bulkOperationsForm.setRef}
						xcss={containerStyles}
					/>
				</Box>
			) : null,
		[bulkOperationsForm.setRef, showBulkEditSidebar],
	);

	useEffect(() => {
		if (!sidebarContent) {
			return;
		}

		if (!lastSetSidebarContent.current) {
			resetSidebarQueryParams();
		}

		if (content !== sidebarContent) {
			setSidebarContent(sidebarContent);
		}
	}, [content, resetSidebarQueryParams, setSidebarContent, sidebarContent]);

	useEffect(() => {
		if (sidebarContent) {
			return;
		}

		if (content && content === lastSetSidebarContent.current) {
			resetSidebarContent();
		}
	}, [content, resetSidebarContent, sidebarContent]);

	return null;
};

const wrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	inset: 'space.0',
	justifyContent: 'stretch',
});

const containerStyles = xcss({
	height: '100%',
	flexGrow: 1,
	// Adjust for the unwanted vertical padding from 'SidebarLayout'
	marginBottom: 'space.negative.100',
	marginTop: 'space.negative.100',
});
